import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Styles/Gallery.css';

const projects = [
  {
    id: 1,
    title: "Queue Management System",
    category: "Fully Functional Apps",
    image: "/lines/leap1.png",
    description: "A revolutionary queue management system designed to streamline virtual lines across multiple industries.",
    youtubeUrl: "https://www.youtube.com/embed/cBNPBTt-Mic?start=467"
  },
  {
    id: 2,
    title: "Fitness Website",
    category: "Full Websites",
    image: "/fitness/fit1.png",
    description: "Build confidence and achieve your fitness goals with our expertly crafted website solutions.",
    video: "/fitness/trueForm.mov"
  },
  {
    id: 4,
    title: "Threshold Studio",
    category: "Full Websites",
    image: "/threshold/ThresholdStudio.mov",
    description: "A sophisticated studio website showcasing creative excellence and professional services.",
    video: "/threshold/ThresholdStudio.mov"
  },
  {
    id: 5,
    title: "Advanced Metal",
    category: "Full Websites",
    image: "/advancedmetal/Advancedmetal.mov",
    description: "Industrial website featuring advanced metalwork services and manufacturing capabilities.",
    video: "/advancedmetal/Advancedmetal.mov"
  },
  {
    id: 6,
    title: "Clockway Desktop",
    category: "Fully Functional Apps",
    image: "/clockway/ClockwayDesktop.mov",
    description: "Desktop time management application with advanced scheduling and productivity features.",
    video: "/clockway/ClockwayDesktop.mov"
  },
  {
    id: 7,
    title: "Clockway Mobile",
    category: "Fully Functional Apps", 
    image: "/clockway/CLockwayMobile.mov",
    description: "Mobile companion app for seamless time tracking and task management on the go.",
    video: "/clockway/CLockwayMobile.mov"
  },
  {
    id: 8,
    title: "Valenofit",
    category: "Full Websites",
    image: "/fitness/valenofit.mov",
    description: "Premium fitness and wellness platform designed to inspire and guide your health journey.",
    video: "/fitness/valenofit.mov"
  },
];

const categories = ["All", "Fully Functional Apps", "Full Websites"];

function Gallery() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = selectedCategory === "All"
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  const handleImageClick = (imageSrc) => {
    setExpandedImage(imageSrc);
  };

  const handleCloseExpandedImage = () => {
    setExpandedImage(null);
  };

  return (
    <div className="gallery-page">
      {/* Fixed Header - matches home page exactly */}
      <header className="fixed-header">
        <div className="logo-container">
          <img src={`${process.env.PUBLIC_URL}/advLogo.png`} alt="Advanced Tech Logo" className="header-logo" />
        </div>
        <nav className="navigation">
          <button onClick={() => navigate('/')}>Home</button>
        </nav>
      </header>

      {/* Gallery Intro Section */}
      <section className="gallery-intro">
        <div className="gallery-hero-background">
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
            <div className="shape shape-5"></div>
            <div className="shape shape-6"></div>
            <div className="shape shape-7"></div>
            <div className="shape shape-8"></div>
            <div className="shape shape-9"></div>
            <div className="shape shape-10"></div>
            <div className="shape shape-11"></div>
            <div className="shape shape-12"></div>
            <div className="shape shape-13"></div>
            <div className="shape shape-14"></div>
            <div className="shape shape-15"></div>
            <div className="shape shape-16"></div>
            <div className="shape shape-17"></div>
            <div className="shape shape-18"></div>
            <div className="shape shape-19"></div>
            <div className="shape shape-20"></div>
          </div>
        </div>
        <div className="hero-content">
          <h1>Project Gallery</h1>
          <p>Explore my portfolio of innovative digital solutions and creative projects</p>
        </div>
      </section>
      
      {/* Gallery Content Container */}
      <div className="gallery-container">
        <div className="category-filters">
          {categories.map(category => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="projects-grid">
          {filteredProjects.map(project => (
            <div 
              key={project.id} 
              className="project-card"
              onClick={() => setSelectedProject(project)}
            >
              <div className="project-image">
                {project.video ? (
                  <video src={project.video} className="project-video" muted />
                ) : (
                  <img src={project.image} alt={project.title} />
                )}
              </div>
              <div className="project-info">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <span className="category-tag">{project.category}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div className="project-modal" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedProject(null)}>×</button>
            <h2>{selectedProject.title}</h2>
            <p>{selectedProject.description}</p>
            {selectedProject.youtubeUrl ? (
              <iframe
                className="modal-video"
                src={selectedProject.youtubeUrl}
                title={selectedProject.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : selectedProject.video ? (
              <video 
                controls 
                className="modal-video"
                autoPlay
                playsInline
                controlsList="nodownload"
                disablePictureInPicture
              >
                <source src={selectedProject.video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="modal-gallery">
                {selectedProject.images?.map((img, index) => (
                  <img 
                    key={index} 
                    src={img} 
                    alt={`${selectedProject.title} ${index + 1}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(img);
                    }}
                    className={expandedImage === img ? 'expanded' : ''}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {expandedImage && (
        <div className="expanded-overlay" onClick={handleCloseExpandedImage}>
          <button className="expanded-close-btn" onClick={handleCloseExpandedImage}>×</button>
          <img 
            src={expandedImage} 
            alt="Expanded view" 
            className="expanded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Gallery; 